import { Link } from "@inertiajs/react";
import { Mail } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import SignInCard from "@/components/auth/SignInCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function ParentMagicLinkSignin() {
  return (
    <>
      <p>
        PagePledge has no passwords. To sign in, type your email and we'll send you a link. Click it within 24 hours and you're in. After that you stay signed in for 30 days, so you'll only do this every few weeks at most.
      </p>

      <h2>How it works</h2>
      <Steps>
        <Step title="Go to pagepledge.com/sign_in and type your email">
          <p>The same email you used when you first signed up.</p>
          <Screenshot caption="The sign-in page" browser="pagepledge.com/sign_in" padded={false}>
            <div className="p-4 max-w-sm mx-auto">
              <SignInCard email="parent@example.com" />
            </div>
          </Screenshot>
        </Step>
        <Step title="Open the email we send you">
          <p>It comes from hello@pagepledge.com — subject "Your PagePledge sign-in link."</p>
          <Screenshot caption="The sign-in email" padded={false}>
            <div className="p-4 max-w-sm mx-auto">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <strong className="text-foreground">PagePledge</strong>
                    <span>&lt;hello@pagepledge.com&gt;</span>
                  </div>
                  <p className="font-display text-base font-semibold text-foreground">Sign in to PagePledge</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Click the button below to sign in. The link works for 24 hours.
                  </p>
                  <Button>Sign in to PagePledge</Button>
                </CardContent>
              </Card>
            </div>
          </Screenshot>
        </Step>
        <Step title="Click the button">
          <p>You're back on PagePledge, signed in, on your dashboard.</p>
        </Step>
      </Steps>

      <h2>Why no passwords?</h2>
      <p>
        Passwords get forgotten, reused across sites, and stolen in big leaks. We didn't think parents should have to remember a new one just for a school read-a-thon. Email links are just as safe — only you can read your inbox.
      </p>

      <h2>A few things to know</h2>
      <p>
        Links work for 24 hours. If yours expired, ask for another one. Once signed in, you stay in for 30 days. Your email is the same account everywhere — even if your school later invites you as a teacher, it's still the same sign-in.
      </p>
      <p>
        Didn't get an email? Check your spam, then try again. We do limit how often you can ask for a link (so the inbox can't be spammed), but legitimate requests always go through.
      </p>

      <Callout variant="privacy" title="What if someone else types your email?">
        <p>
          Nothing useful — only you can read your inbox, and links only work once. The page won't even tell them whether the email is on PagePledge or not; it just says "check your inbox."
        </p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/parent-dashboard">Your dashboard</Link>.</li>
        <li><Link href="/help/articles/multiple-children">Adding more than one child</Link>.</li>
      </ul>
    </>
  );
}
